import { Link } from 'react-router-dom';
import { SafeHtml } from '@/components/SafeHtml';
import { useAuth } from '@/hooks/useAuth';
import { ContractSignaturePad } from './ContractSignaturePad';
import { LegalPolicyConsentField } from '@/features/legal-policies/LegalPolicyConsentField';
import type { LegalPolicyConsentItem } from '@/features/legal-policies/types';

interface ContractSigningViewProps {
  templateTitle: string;
  contractHtml: string;
  firmarRequired: boolean;
  legalPolicies: LegalPolicyConsentItem[];
  acceptedLegalPolicyIds: Set<string>;
  onLegalPolicyToggle: (policyId: string, accepted: boolean) => void;
  acceptedRead: boolean;
  onAcceptedReadChange: (value: boolean) => void;
  onSignatureChange: (signatureData: string | null) => void;
  onAccept: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
}

export function ContractSigningView({
  templateTitle,
  contractHtml,
  firmarRequired,
  legalPolicies,
  acceptedLegalPolicyIds,
  onLegalPolicyToggle,
  acceptedRead,
  onAcceptedReadChange,
  onSignatureChange,
  onAccept,
  onCancel,
  isSubmitting,
  canSubmit,
}: ContractSigningViewProps) {
  const { logout } = useAuth();

  return (
    <div className="lms-contract-signing-page font-sans">
      <header className="lms-contract-signing-header">
        <div className="lms-site-admin-hub-container lms-contract-signing-header__inner">
          {!firmarRequired ? (
            <Link
              to="/mis-contratos"
              className="lms-contract-signing-back"
              aria-label="Volver a mis contratos"
            >
              <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
            </Link>
          ) : (
            <div className="lms-contract-signing-back-spacer" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="lms-contract-signing-title">{templateTitle}</h1>
          </div>
          <button
            type="button"
            className="lms-contract-signing-logout"
            onClick={() => logout()}
          >
            <span className="material-symbols-outlined" aria-hidden="true">logout</span>
            Cerrar sesión
          </button>
        </div>
      </header>

      <div className="lms-site-admin-hub-container">
        <main className="lms-contract-signing-main py-8 md:py-12">
          <section className="lms-contract-signing-terms" aria-labelledby="contract-terms-heading">
            <div className="lms-contract-signing-terms__head">
              <h2 id="contract-terms-heading" className="lms-contract-signing-terms__title">
                Términos y Condiciones del Servicio
              </h2>
              <p className="lms-contract-signing-terms__intro">
                Por favor, lea cuidadosamente el siguiente contrato antes de proceder con su matrícula.
              </p>
            </div>
            <div className="lms-contract-signing-terms__scroll terms-scroll">
              <SafeHtml
                html={contractHtml}
                className="contracts-print-area contracts-signing-view prose max-w-none"
              />
            </div>
          </section>

          <aside className="lms-contract-signing-sidebar">
            <div className="lms-contract-signing-panel">
              <ContractSignaturePad
                layout="contratovista"
                onChange={onSignatureChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="lms-contract-signing-panel lms-contract-signing-acceptance">
              <div className="lms-legal-policy-consent-list">
                {legalPolicies.length > 0 ? (
                  <LegalPolicyConsentField
                    policies={legalPolicies}
                    acceptedIds={acceptedLegalPolicyIds}
                    onToggle={onLegalPolicyToggle}
                    disabled={isSubmitting}
                    layout="contratovista"
                    unified
                  />
                ) : null}
                <div className="lms-legal-policy-consent-item">
                  <label className="lms-contract-signing-checkbox">
                    <span className="lms-contract-signing-checkbox__box">
                      <input
                        type="checkbox"
                        checked={acceptedRead}
                        onChange={(e) => onAcceptedReadChange(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="material-symbols-outlined lms-contract-signing-checkbox__icon" aria-hidden="true">
                        check
                      </span>
                    </span>
                    <span className="lms-contract-signing-checkbox__label">
                      He leído y acepto los términos del contrato de prestación de servicios.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="lms-contract-signing-actions">
              {!firmarRequired ? (
                <button
                  type="button"
                  className="lms-contract-signing-btn lms-contract-signing-btn--cancel"
                  disabled={isSubmitting}
                  onClick={onCancel}
                >
                  Cancelar
                </button>
              ) : null}
              <button
                type="button"
                className="lms-contract-signing-btn lms-contract-signing-btn--accept"
                disabled={!canSubmit}
                onClick={onAccept}
              >
                {isSubmitting ? 'Procesando…' : 'Aceptar y Matricular'}
              </button>
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
}
